/**
 * Magic-link login modal (sc-3015).
 *
 * Three states cycle in-place:
 *   - input    : email field + SEND MAGIC LINK button
 *   - sending  : disabled button + spinner text
 *   - sent     : "CHECK YOUR INBOX" confirmation + CLOSE
 *   - error    : failure text + RETRY
 *
 * Built with DOM (createElement + textContent — no innerHTML, no
 * untrusted-string interpolation). Styles live in shell.css under the
 * `.login-modal` block so the visual matches the install-overlay /
 * pause-overlay aesthetic (amber border, mono font, dark backdrop).
 *
 * Esc closes. Focus is trapped inside the modal while open and
 * restored to the previously-focused element on close.
 */

import { AuthApiError, sendMagicLink } from './api-client.js';

type State = 'input' | 'sending' | 'sent' | 'error';

interface ModalRefs {
  root: HTMLDivElement;
  card: HTMLDivElement;
  title: HTMLHeadingElement;
  body: HTMLParagraphElement;
  emailInput: HTMLInputElement;
  submitButton: HTMLButtonElement;
  closeButton: HTMLButtonElement;
  error: HTMLParagraphElement;
}

let openModal: { close: () => void } | null = null;

/**
 * Open the login modal. If one is already open, focuses it instead of
 * stacking. Returns a handle with `close()` for programmatic dismiss.
 */
export function openLoginModal(): { close: () => void } {
  if (openModal) {
    return openModal;
  }
  const previouslyFocused = document.activeElement as HTMLElement | null;
  const refs = build();
  document.body.appendChild(refs.root);

  // Focus the email field on mount.
  setTimeout(() => refs.emailInput.focus(), 0);

  let state: State = 'input';
  const render = (next: State, message?: string): void => {
    state = next;
    refs.error.textContent = '';
    switch (state) {
      case 'input':
        refs.title.textContent = 'SIGN IN';
        refs.body.textContent =
          'Enter your email — we will send you a magic link.';
        refs.emailInput.disabled = false;
        refs.submitButton.disabled = false;
        refs.submitButton.textContent = 'SEND MAGIC LINK';
        refs.emailInput.hidden = false;
        refs.closeButton.textContent = 'CANCEL';
        break;
      case 'sending':
        refs.body.textContent = 'Sending…';
        refs.emailInput.disabled = true;
        refs.submitButton.disabled = true;
        refs.submitButton.textContent = 'SENDING…';
        break;
      case 'sent':
        refs.title.textContent = 'CHECK YOUR INBOX';
        refs.body.textContent = `We sent a sign-in link to ${refs.emailInput.value}. Click it to finish signing in.`;
        refs.emailInput.hidden = true;
        refs.submitButton.hidden = true;
        refs.closeButton.textContent = 'CLOSE';
        setTimeout(() => refs.closeButton.focus(), 0);
        break;
      case 'error':
        refs.body.textContent = 'Something went wrong sending your magic link.';
        refs.error.textContent = message ?? 'Unknown error';
        refs.emailInput.disabled = false;
        refs.submitButton.disabled = false;
        refs.submitButton.textContent = 'TRY AGAIN';
        break;
    }
  };

  const close = (): void => {
    if (refs.root.parentNode) {
      refs.root.parentNode.removeChild(refs.root);
    }
    document.removeEventListener('keydown', onKeydown, true);
    openModal = null;
    previouslyFocused?.focus?.();
  };

  const onKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      // Don't bubble Escape to the cabinet's pause-request listener —
      // the player meant "close this modal", not "pause the game".
      e.stopPropagation();
      e.preventDefault();
      close();
      return;
    }
    // Trap Tab within the modal.
    if (e.key === 'Tab') {
      const focusable = Array.from(
        refs.root.querySelectorAll<HTMLElement>(
          'input:not([disabled]), button:not([disabled]):not([hidden]), a[href]'
        )
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first?.focus();
      }
    }
  };
  document.addEventListener('keydown', onKeydown, true);

  refs.closeButton.addEventListener('click', close);
  refs.root.addEventListener('click', (e) => {
    // Click outside the card closes (matches install-overlay behavior).
    if (e.target === refs.root) close();
  });

  refs.submitButton.addEventListener('click', () => void submit());

  const submit = async (): Promise<void> => {
    if (state === 'sending') return;
    const email = refs.emailInput.value.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      render('error', 'Enter a valid email address.');
      refs.emailInput.focus();
      return;
    }
    render('sending');
    try {
      await sendMagicLink({
        email,
        redirectUrl: `${window.location.origin}/auth/callback`,
      });
      render('sent');
    } catch (err) {
      const message =
        err instanceof AuthApiError
          ? `${err.code}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Unknown error';
      render('error', message);
    }
  };

  // Submit on Enter inside the input.
  refs.emailInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void submit();
    }
  });

  render('input');
  openModal = { close };
  return openModal;
}

function build(): ModalRefs {
  const root = document.createElement('div');
  root.className = 'login-modal';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-labelledby', 'login-modal-title');

  const card = document.createElement('div');
  card.className = 'login-modal__card';

  const title = document.createElement('h2');
  title.id = 'login-modal-title';
  title.className = 'login-modal__title';
  title.textContent = 'SIGN IN';

  const body = document.createElement('p');
  body.className = 'login-modal__body';

  const emailInput = document.createElement('input');
  emailInput.type = 'email';
  emailInput.placeholder = 'you@example.com';
  emailInput.autocomplete = 'email';
  emailInput.required = true;
  emailInput.className = 'login-modal__input';
  emailInput.setAttribute('aria-label', 'Email address');

  const submitButton = document.createElement('button');
  submitButton.type = 'button';
  submitButton.className = 'login-modal__btn login-modal__btn--primary';

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = 'login-modal__btn';

  const error = document.createElement('p');
  error.className = 'login-modal__error';
  error.setAttribute('role', 'alert');

  const actions = document.createElement('div');
  actions.className = 'login-modal__actions';
  actions.appendChild(closeButton);
  actions.appendChild(submitButton);

  card.appendChild(title);
  card.appendChild(body);
  card.appendChild(emailInput);
  card.appendChild(error);
  card.appendChild(actions);
  root.appendChild(card);

  return { root, card, title, body, emailInput, submitButton, closeButton, error };
}
